import React, { useState, useEffect } from "react";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import { useSelector } from "react-redux";
import moment from "moment";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BoxContainer,
  BoxHead,
  BoxTitle,
  Underline,
} from "../../Components/Styles/PageStyles";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Loading from "../../Components/Loading";
import NotFoundModel from "../../Components/NotFoundModel";
import { InputGroup, Label } from "../../Components/Styles/InputStyles";
import Images from "../../Images";
import { MdKeyboardArrowDown } from "react-icons/md";
import { TbArrowBackUp } from "react-icons/tb";
import tw from "tailwind-styled-components";
import { IoChevronBackOutline } from "react-icons/io5";

const localizer = momentLocalizer(moment);

const CustomModal = ({
  isOpen,
  onClose,
  title,
  children,
  width = "w-full max-w-md",
}) => {
  if (!isOpen) return null;

  return (
    <ModalWrapper>
      <ModalContainer>
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <Overlay onClick={onClose} />
        </div>

        <HiddenSpan aria-hidden="true">&#8203;</HiddenSpan>

        <ModalBox className={width}>
          <ModalContent>
            <ModalBody>
              <InnerBody>
                <CustomTitle>{title}</CustomTitle>
                <ChildrenWrapper>{children}</ChildrenWrapper>
              </InnerBody>
            </ModalBody>
          </ModalContent>
        </ModalBox>
      </ModalContainer>
    </ModalWrapper>
  );
};

const CustomToolbar = ({ label }) => {
  return (
    <div className="rbc-toolbar flex justify-center py-2 text-lg font-semibold">
      {label}
    </div>
  );
};

const StaffAttendance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [staffName, setStaffName] = useState("");

  useEffect(() => {
    if (location.state && location.state.staffName) {
      setStaffName(location.state.staffName);
    }
  }, [location]);

  const user = useSelector((state) => state.UserReducer.user);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [apiData, setApiData] = useState([]);
  const { staffId } = useParams();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState([]);

  const month = moment(selectedDate).month() + 1;
  const year = moment(selectedDate).year();
  const query = `m=${month}&y=${year}`;

  const fetchFunction = async () => {
    const response = await axios.get(
      `${Config.apiUrl}/client/list/particular/staff/attendance/${staffId}?${query}`,
      {
        headers: {
          authorization: `Bearer ${user.token}`,
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        },
      }
    );
    return response.data;
  };

  const calculateWorkingHours = (punchIn, punchOut) => {
    if (
      punchIn === "-" ||
      punchOut === "-" ||
      punchIn === "--" ||
      punchOut === "--"
    ) {
      return "0h 0m";
    }

    const inTime = moment(punchIn, "hh:mm A");
    const outTime = moment(punchOut, "hh:mm A");

    if (outTime.isBefore(inTime)) {
      outTime.add(1, "day");
    }

    const duration = moment.duration(outTime.diff(inTime));
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();

    return `${hours}h ${minutes}m`;
  };

  const getSuccess = (res) => {
    if (res.isSuccess) {
      const groupedByDate = {};

      res.data.forEach((item) => {
        const date = item.date;
        if (!groupedByDate[date]) {
          groupedByDate[date] = [];
        }
        groupedByDate[date].push(item);
      });

      const formattedData = Object.keys(groupedByDate).map((date) => {
        const dayEntries = groupedByDate[date];
        const hasValidEntry = dayEntries.some(
          (entry) =>
            entry.punchIn !== "-" &&
            entry.punchIn !== "--" &&
            entry.punchOut !== "-" &&
            entry.punchOut !== "--"
        );

        const hasPartialEntry = dayEntries.some(
          (entry) =>
            entry.punchIn !== "-" &&
            entry.punchIn !== "--" &&
            (entry.punchOut === "-" || entry.punchOut === "--")
        );

        let title = "Absent";
        let backgroundColor = "#F44336";

        if (hasValidEntry) {
          title = "Present";
          backgroundColor = "#4CAF50";
        } else if (hasPartialEntry) {
          title = "Partial";
          backgroundColor = "#2196F3";
        }

        let totalWorkingHours = "0h 0m";
        if (hasValidEntry || hasPartialEntry) {
          const totalMinutes = dayEntries.reduce((total, entry) => {
            if (
              entry.punchIn !== "-" &&
              entry.punchIn !== "--" &&
              entry.punchOut !== "-" &&
              entry.punchOut !== "--"
            ) {
              const inTime = moment(entry.punchIn, "hh:mm A");
              const outTime = moment(entry.punchOut, "hh:mm A");

              if (outTime.isBefore(inTime)) {
                outTime.add(1, "day");
              }

              return total + outTime.diff(inTime, "minutes");
            }
            return total;
          }, 0);

          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          totalWorkingHours = `${hours}h ${minutes}m`;
        }

        return {
          title,
          start: moment(date, "DD-MM-YYYY").toDate(),
          allDay: true,
          backgroundColor,
          dayEntries,
          totalWorkingHours,
          hasMultipleEntries: dayEntries.length > 1,
        };
      });

      setApiData(formattedData);
    }
  };

  const {
    mutate: getListMutate,
    isLoading,
    error,
  } = useMutation(fetchFunction, {
    onSuccess: getSuccess,
  });

  useEffect(() => {
    getListMutate();
  }, [selectedDate]);

  const handleEventClick = (event) => {
    setSelectedDayData(event.dayEntries);
    setModalOpen(true);
  };

  const CustomEvent = ({ event }) => (
    <EventCard>
      <div className="flex justify-between">
        <Title title={event.title}>{event.title}</Title>
        {event.title !== "Absent" && (
          <ViewDetails onClick={() => handleEventClick(event)}>
            View Details
          </ViewDetails>
        )}
      </div>
      {event.title !== "Absent" && (
        <Meta>
          <div>Total: {event.totalWorkingHours}</div>
        </Meta>
      )}
    </EventCard>
  );

  return (
    <BoxContainer>
      <BoxHead>
        <BoxTitle className="!mb-0 whitespace-nowrap ">
          <BackWrapper>
            <BackButton onClick={() => navigate(-1)}>
              <IoChevronBackOutline size={20} />
            </BackButton>
            <div>
              <span className="flex items-center">
                {staffName}'s Attendance
              </span>
              <Underline />
            </div>
          </BackWrapper>
        </BoxTitle>
        <div className="flex flex-col justify-end">
          <InputGroup>
            <div className="relative items-center">
              <DatePicker
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                className="border border-gray-400 p-2 rounded-md w-40 h-10 bg-white cursor-pointer pl-8"
                maxDate={new Date()}
                onFocus={(e) => e.target.blur()}
                showFullMonthYearPicker
                showTwoColumnMonthYearPicker
                onClickOutside={() => setCalendarOpen(false)}
                onSelect={() => setCalendarOpen(false)}
                open={calendarOpen}
                onInputClick={() => setCalendarOpen(true)}
                showPopperArrow={false}
                portalId="root-portal"
                popperClassName="react-datepicker-popper"
              />
              <img
                src={Images.Calender}
                alt="Calender icon"
                className="w-4 absolute left-2 top-3 text-gray-500"
              />
              <MdKeyboardArrowDown
                className="absolute right-2 top-3 md:block hidden text-gray-500"
                style={{ fontSize: "18px", cursor: "pointer" }}
              />
            </div>
          </InputGroup>
        </div>
      </BoxHead>

      {!error && !isLoading && (
        <Calendar
          localizer={localizer}
          events={apiData}
          startAccessor="start"
          endAccessor="start"
          views={{ month: true }}
          defaultView="month"
          date={moment(selectedDate).startOf("month").toDate()}
          components={{
            event: CustomEvent,
            toolbar: CustomToolbar,
          }}
          eventPropGetter={(event) => {
            return {
              style: {
                backgroundColor: event.backgroundColor,
                color: "#fff",
                borderRadius: "4px",
              },
            };
          }}
        />
      )}

      {isLoading && <Loading />}
      {(error || apiData.length === 0) && !isLoading && <NotFoundModel />}

      <CustomModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Attendance Details - ${selectedDayData[0]?.date}`}
        width="w-full max-w-2xl"
      >
        <ScrollArea>
          <HeaderRow>
            <HeaderCell>Property</HeaderCell>
            <HeaderCell>Punch In</HeaderCell>
            <HeaderCell>Punch Out</HeaderCell>
            <HeaderCell>Working Hours</HeaderCell>
          </HeaderRow>

          {selectedDayData.length === 0 ? (
            <EmptyState>No attendance entries for this day.</EmptyState>
          ) : (
            selectedDayData.map((entry, index) => (
              <Row key={index}>
                <Cell>
                  <FieldLabel>Property</FieldLabel>
                  <PropertyValue title={entry.propertyName}>
                    {entry.propertyName}
                  </PropertyValue>
                </Cell>

                <Cell>
                  <FieldLabel>Punch In</FieldLabel>
                  <TimeValue>{entry.punchIn}</TimeValue>
                </Cell>

                <Cell>
                  <FieldLabel>Punch Out</FieldLabel>
                  <TimeValue>{entry.punchOut}</TimeValue>
                </Cell>

                <Cell>
                  <FieldLabel>Working Hours</FieldLabel>
                  <TimeValue>
                    {calculateWorkingHours(entry.punchIn, entry.punchOut)}
                  </TimeValue>
                </Cell>
              </Row>
            ))
          )}
        </ScrollArea>
      </CustomModal>
    </BoxContainer>
  );
};
const ScrollArea = tw.div`max-h-96 md:max-h-[70vh] overflow-y-auto`;
const HeaderRow = tw.div`hidden md:grid grid-cols-4 gap-4 px-4 py-2 text-xs font-medium text-gray-500sticky top-0 bg-white border-b`;
const HeaderCell = tw.div`text-left`;
const Row = tw.div`grid grid-cols-2 md:grid-cols-4 items-start gap-4 px-4 py-3 border-b last:border-b-0`;
const Cell = tw.div`min-w-0`;
const FieldLabel = tw.p`text-[12px] text-gray-500 md:hidden`;
const FieldValue = tw.p`text-sm text-gray-900`;
const TimeValue = tw(FieldValue)`font-mono tabular-nums`;
const PropertyValue = tw(FieldValue)`truncate`;
const EmptyState = tw.div`px-4 py-10 text-center text-sm text-gray-500`;
const EventCard = tw.div`w-full p-1 cursor-pointer text-white leading-snug`;
const Title = tw.h2`text-[12px] font-semibold truncate`;
const Meta = tw.div`text-[11px] mt-0.5 space-y-0.5`;
const ViewDetails = tw.div`text-[12px] text-center underline`;
const Overlay = tw.div`fixed inset-0 bg-gray-500 opacity-75`;
const ModalWrapper = tw.div`fixed inset-0 z-50 overflow-y-auto`;
const ModalContainer = tw.div`flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0`;
const HiddenSpan = tw.span`hidden sm:inline-block sm:align-middle sm:h-screen`;
const ModalBox = tw.div`inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle`;
const ModalContent = tw.div`px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4`;
const ModalBody = tw.div`sm:flex sm:items-start`;
const InnerBody = tw.div`w-full mt-3 text-center sm:mt-0 sm:text-left`;
const CustomTitle = tw.h3`text-lg font-medium leading-6 text-gray-900`;
const ChildrenWrapper = tw.div`mt-4`;
const BackWrapper = tw.div`flex items-center`;
const BackButton = tw.button`hover:bg-gray-200 items-center -mt-1 p-1 rounded-full transition-colors`;
export default StaffAttendance;
